import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';
import Sidebar from './components/Sidebar';
import MobileHeader from './components/MobileHeader';
import Footer from './components/Footer';
import BackToTopButton from './components/BackToTopButton';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import EventsPage from './pages/EventsPage';
import MembershipPage from './pages/MembershipPage';
import GalleryPage from './pages/GalleryPage';
import GrievancePage from './pages/GrievancePage';
import NewsPage from './pages/NewsPage';
import PoliciesPage from './pages/PoliciesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ContactPage from './pages/ContactPage';
import RegisterPage from './pages/RegisterPage';
import RenewalPage from './pages/RenewalPage';
import NewsDetailPage from './pages/NewsDetailPage';
import EventDetailPage from './pages/EventDetailPage';
import EventRegistrationPage from './pages/EventRegistrationPage';
import PartnersPage from './pages/PartnersPage';

import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminNewsPage from './pages/admin/AdminNewsPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminEventRegistrationsPage from './pages/admin/AdminEventRegistrationsPage';
import AdminContactPage from './pages/admin/AdminContactPage';
import AdminGrievancesPage from './pages/admin/AdminGrievancesPage';
import AdminMembersPage from './pages/admin/AdminMembersPage';
import AdminGalleryPage from './pages/admin/AdminGalleryPage';
import AdminMembershipPlansPage from './pages/admin/AdminMembershipPlansPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import AdminServicesPage from './pages/admin/AdminServicesPage';
import AdminCompaniesPage from './pages/admin/AdminCompaniesPage';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
import './styles/main.css';

// Import all service detail pages
import MPCBServicePage from './pages/services/MPCBServicePage';
import DISHServicePage from './pages/services/DISHServicePage';
import DICServicePage from './pages/services/DICServicePage';
import CGWAServicePage from './pages/services/CGWAServicePage';
import MIDCServicePage from './pages/services/MIDCServicePage';
import MSEBServicePage from './pages/services/MSEBServicePage';
import LaborServicePage from './pages/services/LaborServicePage';
import FairPriceServicePage from './pages/services/FairPriceServicePage';
import HazardousWasteManagementServicePage from './pages/services/HazardousWasteManagementServicePage';

// Import About Us submenu pages
import PresidentPage from './pages/about/PresidentPage';
import MissionVisionPage from './pages/about/MissionVisionPage';
import CoreValuesPage from './pages/about/CoreValuesPage';
import StructurePage from './pages/about/StructurePage';
import LeadershipTeamPage from './pages/about/LeadershipTeamPage';
// import AchievementsPage from './pages/about/AchievementsPage';
import CommitteePage from './pages/about/CommitteePage';
// import FounderPage from './pages/about/FounderPage';

// Admin Layout Component
const AdminLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getPageTitle = () => {
    const titles = {
      '/admin': 'Dashboard',
      '/admin/news': 'News',
      '/admin/events': 'Events',
      '/admin/event-registrations': 'Event Registrations',
      '/admin/services': 'Services',
      '/admin/members': 'Members',
      '/admin/membership-plans': 'Membership Plans',
      '/admin/companies': 'Companies',
      '/admin/gallery': 'Gallery',
      '/admin/payments': 'Payments',
      '/admin/contact-submissions': 'Contact Enquiries',
      '/admin/grievances': 'Grievances',
      '/admin/audit-logs': 'Activity Logs'
    };
    return titles[location.pathname] || 'Admin Panel';
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div style={{ 
        paddingLeft: isMobile ? '0' : '250px',
        flex: 1,
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        minHeight: '100vh',
        background: '#f9fafb',
        overflowX: 'hidden'
      }}>
        {isMobile && (
          <MobileHeader 
            onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
            title={getPageTitle()}
          />
        )}
        <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Main App Content Component
function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') && location.pathname !== '/admin/login';

  return (
    <div className="App">
      {!isAdminRoute && <Navigation />}
      {!isAdminRoute && <BackToTopButton />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/about/president" element={<PresidentPage />} />
        <Route path="/about/mission-vision" element={<MissionVisionPage />} />
        <Route path="/about/core-values" element={<CoreValuesPage />} />
        <Route path="/about/structure" element={<StructurePage />} />
        <Route path="/about/leadership-team" element={<LeadershipTeamPage />} />
        {/* <Route path="/about/achievements" element={<AchievementsPage />} /> */}
        <Route path="/about/committee" element={<CommitteePage />} />
        {/* <Route path="/about/founder" element={<FounderPage />} /> */}
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:id" element={<EventDetailPage />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/grievance" element={<GrievancePage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:id" element={<NewsDetailPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-conditions" element={<TermsConditionsPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/member-registration" element={<RegisterPage />} />
        <Route path="/renewal" element={<RenewalPage />} />
        <Route path="/renew-membership" element={<RenewalPage />} />
        <Route path="/event-registration/:id" element={<EventRegistrationPage />} />
        
        {/* Service Detail Pages */}
        <Route path="/services/mpcb" element={<MPCBServicePage />} />
        <Route path="/services/dish" element={<DISHServicePage />} />
        <Route path="/services/dic" element={<DICServicePage />} />
        <Route path="/services/cgwa" element={<CGWAServicePage />} />
        <Route path="/services/midc" element={<MIDCServicePage />} />
        <Route path="/services/mseb" element={<MSEBServicePage />} />
        <Route path="/services/labor" element={<LaborServicePage />} />
        <Route path="/services/fairprice" element={<FairPriceServicePage />} />
        <Route path="/services/hazardous-waste" element={<HazardousWasteManagementServicePage />} />

        {/* Admin Routes - All Protected */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboardPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/news" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminNewsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/events" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminEventsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/services" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminServicesPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminCompaniesPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/event-registrations" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminEventRegistrationsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/contact-submissions" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminContactPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/grievances" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminGrievancesPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/members" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminMembersPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/gallery" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminGalleryPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/membership-plans" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminMembershipPlansPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/payments" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminPaymentsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/audit-logs" element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminAuditLogsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
      </Routes>
      {!isAdminRoute && <Footer />}
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  const getBasename = () => {
    if (process.env.NODE_ENV === 'development') {
      return '/';
    }
    return '/';
  };

  return (
    <ErrorBoundary>
      <Router basename={getBasename()}>
        <ScrollToTop />
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
}

export default App; 
